import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import routes from '../../utils/routes';
import strings from '../../utils/strings';
import { CText, CHeader } from '../../components/common';
import { FiUser, FiShield, FiClock, FiCreditCard, FiBell, FiGlobe, FiHelpCircle, FiFileText, FiInfo, FiLogOut, FiEdit, FiChevronRight } from 'react-icons/fi';

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);
  const user = useAppSelector((state) => state.user.user);

  const menuItems = [
    {
      section: strings.accountDetails,
      items: [
        { icon: FiUser, title: strings.personalDetails, subtitle: strings.yourAccountInformation, route: routes.personalDetails },
        { icon: FiShield, title: strings.identifyVerification, subtitle: strings.yourVerificationStatus },
        { icon: FiClock, title: strings.transactionHistory, subtitle: strings.yourTransactionDetails },
        { icon: FiCreditCard, title: strings.bankAccount, subtitle: strings.manageYourBankAccount, route: routes.bankAccount },
      ],
    },
    {
      section: strings.settings,
      items: [
        { icon: FiBell, title: strings.pushNotifications, subtitle: strings.notificationPreferences, route: routes.pushNotification },
        { icon: FiGlobe, title: strings.language, subtitle: strings.english, route: routes.selectLanguage },
      ],
    },
    {
      section: strings.others,
      items: [
        { icon: FiHelpCircle, title: strings.helpCenter, subtitle: strings.getSupports, route: routes.helpCenter },
        { icon: FiFileText, title: strings.termsConditions, subtitle: strings.ourTermsConditions, route: routes.termsAndCondition },
        { icon: FiFileText, title: strings.privacyPolicy, subtitle: strings.ourPrivacyPolicy, route: routes.privacyPolicies },
        { icon: FiInfo, title: strings.aboutApp, subtitle: strings.ourApplication, route: routes.aboutApp },
      ],
    },
  ];

  const RightAccessory = () => (
    <div className="mr-2.5 cursor-pointer" onClick={() => navigate(routes.personalDetails)}>
      {React.createElement(FiEdit as any, { size: 20, color: colors.white })}
    </div>
  );

  return (
    <div
      className="min-h-screen pb-28 lg:pb-8"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <div className="lg:max-w-5xl lg:mx-auto">
      {/* Profile Header */}
      <div
        className="h-64 md:h-72 mt-2 flex flex-col items-center"
        style={{
          background: `linear-gradient(135deg, ${colors.gradient1} 0%, ${colors.gradient2} 50%, ${colors.gradient1} 100%)`,
        }}
      >
        <CHeader rightIcon={<RightAccessory />} color={colors.white} />

        {/* Profile Avatar */}
        <div
          className="w-20 h-20 md:w-24 md:h-24 rounded-full mt-6 flex items-center justify-center text-3xl md:text-4xl font-bold"
          style={{
            background: `linear-gradient(135deg, ${colors.gradient1} 0%, ${colors.gradient2} 100%)`,
            color: colors.white,
          }}
        >
          {user?.name?.charAt(0)?.toUpperCase() || 'U'}
        </div>

        {/* User Info */}
        <div className="mt-4 text-center">
          <CText type="B20" color={colors.white}>
            {user?.name || 'User Name'}
          </CText>
          <CText type="R14" color={colors.primary2}>
            {user?.email || 'user@example.com'}
          </CText>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 md:px-6 lg:px-8">
        {/* Invitation Banner */}
        <div
          className="h-20 rounded-xl flex justify-between items-center mt-4 overflow-hidden border"
          style={{ borderColor: colors.primary2 }}
        >
          <CText type="R14" style={{ width: '55%', marginLeft: '20px', lineHeight: '1.5' }}>
            Invite friends and earn rewards together!
          </CText>
          <div
            className="h-20 w-24 flex items-center justify-center text-3xl rounded-br-xl"
            style={{ backgroundColor: colors.primary50 }}
          >
            🎁
          </div>
        </div>

        {/* Menu Sections */}
        {menuItems.map((section) => (
          <div key={section.section} className="mb-6">
            <CText type="B16" color={colors.textColor} style={{ marginTop: '16px', marginBottom: '12px' }}>
              {section.section}
            </CText>
            {section.items.map((item) => (
              <div
                key={item.title}
                className="h-16 md:h-18 rounded-xl flex justify-between items-center my-2 px-3 cursor-pointer hover:opacity-80 transition-opacity border"
                style={{
                  borderColor: colors.dark ? colors.grayScale700 : colors.grayScale200,
                }}
                onClick={() => item.route && navigate(item.route)}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="h-10 w-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: colors.inputBackground }}
                  >
                    {React.createElement(item.icon as any, { size: 20, color: colors.primary })}
                  </div>
                  <div>
                    <CText type="B12" color={colors.textColor}>{item.title}</CText>
                    <CText type="R10" color={colors.grayScale500}>{item.subtitle}</CText>
                  </div>
                </div>
                {React.createElement(FiChevronRight as any, { size: 20, color: colors.grayScale500 })}
              </div>
            ))}
          </div>
        ))}

        {/* Logout Button */}
        <div
          className="h-16 rounded-xl flex justify-between items-center mt-6 px-3 cursor-pointer hover:opacity-80 transition-opacity border"
          style={{
            backgroundColor: colors.alertColor + '10',
            borderColor: colors.alertColor,
          }}
          onClick={() => {/* Handle logout */}}
        >
          <div className="flex items-center gap-3">
            <div
              className="h-10 w-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: colors.alertColor + '20' }}
            >
              {React.createElement(FiLogOut as any, { size: 20, color: colors.alertColor })}
            </div>
            <CText type="M14" color={colors.alertColor}>{strings.logOut}</CText>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Profile;
